import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import WrappedQuill from 'components/Quill';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { first } from 'lodash';
import LoadingIcon from 'mdi-react/LoadingIcon';
import Button from 'components/buttons/Button';
import AttachmentIcon from 'icons/attachment';
import DocIcon from 'icons/doc';
import ReplyAllIcon from '@material-ui/icons/ReplyAll';
import ReplyIcon from '@material-ui/icons/Reply';
import ForwardIcon from '@material-ui/icons/Forward';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import Input from 'components/forms/Input';
import emailForwarder from 'constants/templates/emailForwarder';

import { createMessage, uploadImage, getMessage } from 'redux/actions/messageActions';
import { inboxToolbarModules } from 'constants/pitch';
import PrevMessage from './PrevMessage';


const styles = theme => ({
  inputContainer: {
    width: '80%',
    marginLeft: '2rem',
    position: 'relative',
    '& $btnIcon': {
      position: 'absolute',
      top: '0.6rem',
      left: '-2rem',
    },
  },
  labelValueWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  btnIcon: {
    color: theme.palette.secondary['300'],
    marginRight: '0.563rem',
  },
  attachmentsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  attachment: {
    height: 'auto',
    minHeight: '3rem',
    marginTop: '1rem',
    marginRight: '0.5rem',
    padding: '0.875rem 2rem 0.875rem 0.75rem',
    borderRadius: '0.375rem',
    backgroundColor: theme.palette.primary['100'],
    color: theme.palette.primary.main,
    fontSize: '0.875rem',
    position: 'relative',
    maxWidth: '100%',
    display: 'flex',
  },
  docIcon: {
    marginRight: '0.5rem',
  },
  attachmentName: {
    maxWidth: '25ch',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    wordBreak: 'normal',
  },
  closeIcon: {
    position: 'absolute',
    right: '0.75rem',
    width: '1.125rem',
    height: '1.125rem',
    cursor: 'pointer',
  },
  replyFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2.5rem',
  },
  addAttachmentBtn: {
    backgroundColor: theme.palette.white,
    border: `1px solid ${theme.palette.secondary['300']}`,
    borderRadius: '0.375rem',
    width: '2.5rem',
    color: theme.palette.secondary['300'],
    cursor: 'pointer',
  },
});

const setSignature = signature => (signature
  ? `<div><br></div><div><br></div><div>${signature}</div>`
  : ''
);

class MessageEditor extends PureComponent {
  static propTypes = {
    isThreadShadow: PropTypes.bool,
    createMessage: PropTypes.func.isRequired,
    getMessage: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    message: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      id: PropTypes.string,
      outreach: PropTypes.string,
      body_plain: PropTypes.string,
      body_html: PropTypes.string,
    }).isRequired,
    classes: PropTypes.element.isRequired,
    type: PropTypes.string,
    parentMessage: PropTypes.shape({
      id: PropTypes.string,
      reply_status: PropTypes.string,
      email_service_provider_config: PropTypes.number,
      signature: PropTypes.string,
      sent_from_email: PropTypes.string,
    }).isRequired,
    setMessageEditor: PropTypes.func.isRequired,
    toggleInboxDetail: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isThreadShadow: true,
    isDisabled: false,
    type: null,
  };

  constructor(props) {
    super(props);
    const { parentMessage } = props;
    this.state = {
      messageText: setSignature(parentMessage.signature),
      isLoading: false,
      to: '',
      cc: '',
      files: [],
      uploadingImg: false,
    };
    this.editor = React.createRef();
    this.upload = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.type !== this.props.type) {
      if (nextProps.type === false) {
        this.setState({
          messageText: setSignature(this.props.parentMessage.signature),
          cc: '',
          files: [],
        });
      }
      this.setState({ to: this.getInitialToInputValue(nextProps) });
      if (nextProps.type === 'FORWARD') {
        this.setState({ messageText: this.getInitialForwardText(nextProps) });
      }
    }
  }

  onChangeFile = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const { files } = event.target;
    this.setState(prevState => ({
      files: prevState.files.concat(Array.from(files)),
    }));
  }

  setUploading = (value) => {
    this.setState({ uploadingImg: value });
  }

  getMessageTypeIcon = () => {
    const { type } = this.props;
    let Icon = null;
    switch (type) {
      case 'REPLY':
        Icon = ReplyIcon;
        break;
      case 'REPLY_ALL':
        Icon = ReplyAllIcon;
        break;
      case 'FORWARD':
        Icon = ForwardIcon;
        break;
      default:
        break;
    }
    return Icon;
  }

  getInitialForwardText = (props) => {
    const { message } = props;
    return emailForwarder(message) + message.body_html;
  }

  getInitialToInputValue = (props) => {
    const { type, message } = props;
    let inputValue = [];
    switch (type) {
      case 'REPLY':
        inputValue = [message.sent_from_email];
        break;
      case 'REPLY_ALL':
        // eslint-disable-next-line no-case-declarations
        const unique = new Set([
          message.sent_from_email,
          ...(message.sent_to ? message.sent_to.map(to => to.email) : []),
          ...(message.cc ? message.cc.map(to => to.email) : []),
        ]);
        inputValue = [...unique];
        break;
      case 'FORWARD':
        inputValue = [];
        break;
      default:
        break;
    }
    return inputValue.join(', ');
  }

  handleOnChangeImg = allDeltas => (this.setState({
    messageText: allDeltas,
    isLoading: false,
    uploadingImg: false,
  }));
  handleOnChange = value => (this.setState({ messageText: value }));

  handleCreateMessage = async () => {
    const {
      message, parentMessage, setMessageEditor, type,
    } = this.props;
    const { to, cc } = this.state;
    // this is more consistent way, because messageText can contain Delta objects or html
    const newMessage = this.editor.getEditor().root.innerHTML;

    const cleanAndMapEmails = emails =>
      emails
        .split(',')
        .map(email => email.trim())
        .filter(email => email !== '')
        .map(email => ({ name: '', email }));

    const toData = JSON.stringify(cleanAndMapEmails(to));
    const ccData = JSON.stringify(cc !== '' ? cleanAndMapEmails(cc) : null);

    const formData = new FormData();
    formData.append('body_html', newMessage);
    // eslint-disable-next-line no-unused-expressions
    message.outreach && formData.append('outreach', message.outreach);
    formData.append('thread', parentMessage.thread);
    formData.append('sent_to', toData);
    formData.append('cc', ccData);
    formData.append('in_reply_to', message.id);
    this.state.files.map((file, i) => formData.append(`files-${i}`, file));

    if (newMessage) {
      this.setState({ isLoading: true });
      await this.props.createMessage(formData, type);
      await this.props.getMessage(parentMessage.id, true);
      setMessageEditor({ opened: false, message });
      this.setState({ messageText: setSignature(parentMessage.signature), isLoading: false });
      this.props.toggleInboxDetail();
    }
  }

  attachRef = (ref) => {
    this.editor = ref;
  }

  attachUploadRef = (ref) => {
    this.upload = ref;
  }

  toInputChange = (value) => {
    this.setState({ to: value });
  }

  CCInputChange = (value) => {
    this.setState({ cc: value });
  }

  handleRemoveAttachment = (name) => {
    const newFiles = this.state.files.filter(file => file.name !== name);
    this.setState({ files: newFiles });
  }

  render() {
    const {
      isThreadShadow,
      message,
      isDisabled,
      classes,
    } = this.props;
    const {
      messageText, isLoading, files, to,
    } = this.state;

    const newMessageClass = classNames({
      'new-message pr-4 pl-4': true,
      'new-message__shadow--show': isThreadShadow && !isLoading,
      'new-message__shadow--hide': !isThreadShadow,
    });
    const isDisabledFinal = (message && message.loading) || isLoading || isDisabled || !messageText || !to;
    const MessageTypeIcon = this.getMessageTypeIcon();
    return (
      <React.Fragment>
        <div className={classes.inputContainer}>
          {MessageTypeIcon && <MessageTypeIcon className={classes.btnIcon} viewBox="0 0 24 24" />}
          <div className={classes.labelValueWrapper}>
            <div>To:</div>
            <Input
              noBorder
              value={this.state.to}
              onChange={e => this.toInputChange(e.target.value)}
            />
          </div>
          <div className={classes.labelValueWrapper}>
            <div>Cc:</div>
            <Input
              noBorder
              value={this.state.cc}
              onChange={e => this.CCInputChange(e.target.value)}
            />
          </div>
        </div>
        <div className={newMessageClass}>
          {isLoading && <div className="loader"><LoadingIcon /></div>}
          <div className="editor-container">
            <WrappedQuill
              attachRef={this.attachRef}
              onKeyDown={
               e => e.key === 'Enter' && e.ctrlKey && this.props.parentMessage.reply_status !== 'bounced'
                   && this.handleCreateMessage()
              }
              placeholder="Ctrl + Enter - send message&#10;Enter - new line"
              modules={inboxToolbarModules}
              loading={this.state.upload}
              setUploading={this.setUploading}
              value={messageText}
              readOnly={isLoading || isDisabled}
              handleOnChange={this.handleOnChange}
              handleOnChangeImg={this.handleOnChangeImg}
              email={this.props.parentMessage.sent_from_email}
              signature={this.props.parentMessage.signature}
              emailServiceProviderConfigId={this.props.parentMessage.email_service_provider_config}
            />

            {first(document.getElementsByClassName('ql-container ql-snow')) && createPortal(
              <PrevMessage message={message} />,
              first(document.getElementsByClassName('ql-container ql-snow')),
            )}

            <div className={classes.attachmentsContainer}>
              {files.map(file => (
                <div className={classes.attachment}>
                  <DocIcon className={classes.docIcon} />
                  <span className={classes.attachmentName}>{file.name}</span>
                  <CloseRoundedIcon
                    className={classes.closeIcon}
                    onClick={() => this.handleRemoveAttachment(file.name)}
                  />
                </div>
              ))}
            </div>

            <div className={classes.replyFooter}>
              <input
                id="attachmentInput"
                type="file"
                ref={this.attachUploadRef}
                style={{ display: 'none' }}
                onChange={this.onChangeFile}
              />
              <button className={classes.addAttachmentBtn} onClick={() => this.upload.click()}>
                <AttachmentIcon />
              </button>
              <Button
                className="mr-2"
                color="primaryCTA"
                variant="contained"
                onClick={this.handleCreateMessage}
                loading={this.state.uploadingImg}
                disabled={isDisabledFinal}
                size="medium"
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}


export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      parentMessage: state.message,
    }),
    { createMessage, getMessage, uploadImage },
  ),
  withNamespaces('common'),
)(MessageEditor);
